import React, { useState, useEffect } from "react";
import axios from "axios";
import Sidebar from "../layout/Sidebar";
import AgentSidebar from "../layout/AgentSidebar";
import Swal from "sweetalert2";

function AdvancePage() {
  const [advances, setAdvances] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState(
    new Date().toISOString().slice(0, 7)
  );
  const [searchQuery, setSearchQuery] = useState("");
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formData, setFormData] = useState({
    id: null,
    admin_id: "",
    date1: new Date().toISOString().split("T")[0],
    amount: "",
  });

  const role = sessionStorage.getItem("role");
  const SidebarComponent = role === "agent" ? AgentSidebar : Sidebar;
  const baseURL = process.env.REACT_APP_BASE_URL;

  useEffect(() => {
    if (role === "admin") {
      fetchAdvances();
    } else {
      fetchAgentAdvance();
    }
    fetchAdmins();
  }, []);

  const fetchAdvances = async () => {
    try {
      const token = sessionStorage.getItem("access_token");
      const res = await axios.get(`${baseURL}/getAdvanceData`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAdvances(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchAgentAdvance = async () => {
    try {
      const accessToken = sessionStorage.getItem("access_token");
      const agentId = sessionStorage.getItem("agent_id");
      const response = await axios.get(`${baseURL}/getAgentAdvance`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
        params: {
          agent_id: agentId,
        },
      });
      setAdvances(response.data);
      console.log("Agent Advance fetched successfully:", response.data);
    } catch (error) {
      console.error("Error fetching items:", error);
    }
  };

  const fetchAdmins = async () => {
    try {
      const token = sessionStorage.getItem("access_token");
      const res = await axios.get(`${baseURL}/getAllAdmin`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAdmins(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const handleAdd = () => {
    setFormData({
      id: null,
      admin_id: "",
      date1: new Date().toISOString().split("T")[0],
      amount: "",
    });
    setIsModalOpen(true);
  };

  const handleEdit = (advance) => {
    setFormData({
      id: advance.id,
      admin_id: advance.admin_id,
      date1: advance.date1,
      amount: advance.amount,
    });
    setIsModalOpen(true);
  };

  const handleDelete = (item) => {
    Swal.fire({
      title: "Are you sure?",
      text: "Do you really want to delete this record?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
    }).then((result) => {
      if (result.isConfirmed) {
        deleteItem(item.id);
      }
    });
  };

  const deleteItem = async (id) => {
    try {
      const token = sessionStorage.getItem("access_token");
      const response = await axios.delete(`${baseURL}/deleteAdvance`, {
        data: { id: id },
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.status === 200) {
        Swal.fire({
          icon: "success",
          title: "Deleted!",
          text: "The record has been deleted.",
          timer: 2000,
          showConfirmButton: false,
        });
        fetchAdvances();
      } else {
        Swal.fire({
          icon: "error",
          title: "Deletion Failed",
          text: "Something went wrong. Please try again.",
          timer: 3000,
          showConfirmButton: false,
        });
      }
    } catch (error) {
      console.error("Error deleting advance:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Could not delete record",
        timer: 3000,
        showConfirmButton: false,
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = sessionStorage.getItem("access_token");
      if (formData.id) {
        await axios.put(`${baseURL}/updateAdvance`, formData, {
          headers: { Authorization: `Bearer ${token}` },
        });
        Swal.fire("Success", "Advance record updated!", "success");
      } else {
        await axios.post(`${baseURL}/insertAdvance`, formData, {
          headers: { Authorization: `Bearer ${token}` },
        });
        Swal.fire("Success", "Advance record added!", "success");
      }
      setIsModalOpen(false);
      fetchAdvances();
    } catch (err) {
      console.error(err);
      Swal.fire("Error", "Could not save record", "error");
    }
  };

  const handleChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const filteredAdvances = advances.filter((adv) => {
    const recordYearMonth = adv.date1?.substring(0, 7);
    const matchesMonth = recordYearMonth === selectedMonth;
    const matchesSearch = adv.admin_name
      ?.toLowerCase()
      .includes(searchQuery.toLowerCase());
    return matchesMonth && matchesSearch;
  });

  // Pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredAdvances.slice(
    indexOfFirstItem,
    indexOfLastItem
  );
  const totalPages = Math.ceil(filteredAdvances.length / itemsPerPage);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  function formatDate(dateString) {
    if (!dateString) return "";
    const parts = dateString.split("-");
    if (parts.length !== 3) return dateString;
    return `${parts[2]}-${parts[1]}-${parts[0]}`;
  }

  // Determine if editing mode based on formData.id
  const isEditMode = Boolean(formData.id);

  return (
    <div className="bg-gray-100 flex flex-col w-full">
      <SidebarComponent />
      <main className="flex-grow p-6 w-full">
        <h2 className="text-3xl font-bold text-center mt-5">Advance</h2>

        <div className="mt-6 bg-white shadow rounded-lg p-4 ml-0 sm:ml-0 md:ml-28 lg:ml-64">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center space-y-4 sm:space-y-0 mb-4">
            <div className="flex items-center space-x-2">
              <label>Show</label>
              <select
                className="border border-gray-300 rounded p-1"
                onChange={(e) => setItemsPerPage(Number(e.target.value))}
                value={itemsPerPage}
              >
                <option value="10">10</option>
                <option value="25">25</option>
                <option value="50">50</option>
              </select>
              <span>entries</span>
            </div>

            <div className="flex items-center space-x-4">
              {role !== "agent" && (
                <>
                  <button
                    onClick={handleAdd}
                    className="bg-green-500 text-white px-4 py-2 rounded"
                  >
                    Add Advance
                  </button>
                </>
              )}

              <input
                type="month"
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
                className="border border-gray-300 rounded p-1"
              />
              {role !== "agent" && (
                <input
                  type="text"
                  placeholder="Search Admin"
                  className="border border-gray-300 rounded p-1"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              )}
            </div>
          </div>

          {isModalOpen && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white rounded-lg p-6 w-full max-w-lg">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-xl font-semibold">
                    {isEditMode ? "Edit Advance" : "Add Advance"}
                  </h2>
                  <button
                    onClick={() => setIsModalOpen(false)}
                    className="text-gray-600 text-2xl"
                  >
                    &times;
                  </button>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label>Admin</label>
                    <select
                      name="admin_id"
                      value={formData.admin_id}
                      onChange={handleChange}
                      className="border p-2 w-full"
                      required
                    >
                      <option value="">Select Admin</option>
                      {admins.map((admin) => (
                        <option key={admin.id} value={admin.id}>
                          {admin.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label>Date</label>
                    <input
                      type="date"
                      name="date1"
                      value={formData.date1}
                      onChange={handleChange}
                      className="border p-2 w-full"
                      required
                    />
                  </div>

                  <div>
                    <label>Amount</label>
                    <input
                      type="number"
                      name="amount"
                      value={formData.amount}
                      onChange={handleChange}
                      className="border p-2 w-full"
                      required
                    />
                  </div>

                  <div className="flex justify-end space-x-2">
                    <button
                      type="submit"
                      className="bg-blue-500 text-white px-4 py-2 rounded"
                    >
                      {isEditMode ? "Update" : "Submit"}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead className="bg-gray-200">
                <tr>
                  {role !== "agent" && <th className="p-2">Admin</th>}
                  <th className="p-2">Date</th>
                  <th className="p-2">Amount</th>
                  {role !== "agent" && <th className="p-2">Options</th>}
                </tr>
              </thead>
              <tbody>
                {currentItems.length > 0 ? (
                  currentItems.map((adv) => (
                    <tr key={adv.id} className="border-b">
                      {role !== "agent" && (
                        <td className="p-2">{adv.admin_name}</td>
                      )}
                      <td className="p-2">{formatDate(adv.date1)}</td>
                      <td className="p-2">{adv.amount}</td>
                      {role !== "agent" && (
                        <td className="p-2 space-x-2">
                          <button
                            onClick={() => handleEdit(adv)}
                            className="text-blue-500"
                          >
                            <i className="fas fa-edit"></i>
                          </button>
                          <button
                            onClick={() => handleDelete(adv)}
                            className="text-red-500"
                          >
                            <i className="fas fa-trash"></i>
                          </button>
                        </td>
                      )}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="4" className="text-center p-4">
                      No records found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="flex justify-between items-center mt-4">
            <p>
              Showing {indexOfFirstItem + 1} to{" "}
              {indexOfLastItem > filteredAdvances.length
                ? filteredAdvances.length
                : indexOfLastItem}{" "}
              of {filteredAdvances.length} entries
            </p>
            <div className="flex space-x-2">
              <button
                disabled={currentPage === 1}
                onClick={() => handlePageChange(currentPage - 1)}
                className="border px-2 py-1 rounded"
              >
                Previous
              </button>
              {[...Array(totalPages).keys()].map((num) => (
                <button
                  key={num}
                  onClick={() => handlePageChange(num + 1)}
                  className={`border px-2 py-1 rounded ${
                    currentPage === num + 1 ? "bg-blue-500 text-white" : ""
                  }`}
                >
                  {num + 1}
                </button>
              ))}
              <button
                disabled={currentPage === totalPages}
                onClick={() => handlePageChange(currentPage + 1)}
                className="border px-2 py-1 rounded"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

export default AdvancePage;
